/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */
'use strict';

function ContentCollaboratorGridViewController(this: JS5Object, _: JS5Object, $routeParams: JS5Object,
                                               uiGridConstants: JS5Object, CollaboratorViewDataTransformer: JS5Object,
                                               NotificationService: JS5Object, ModalService: JS5Object,
                                               CollaboratorsService: JS5Object, NavbarUtilService: JS5Object,
                                               PaginationService: JS5Object, $location: JS5Object,
                                               ContentEditService: JS5Object) {
    let contentPathParam = _.isEqual(_.get(this, 'content.type'), 'Series') ? 'series' : 'movies';
    this.isSeason = !_.isNil(_.get(this, 'season'));
    this.isSeries = !_.isNil(_.isEqual(_.get(this, 'content.type'), 'Series'));
    const handleGridDataQuery = (gridData: JS5Object, responseHeaders: JS5Function) => {
        PaginationService.parseResponseHeaders(responseHeaders());
        PaginationService.pageData = gridData;
        this.gridOptions.data = gridData;
        if (this.isSeason) {
            ContentEditService.contentObject = this.content;
            ContentEditService.seasonObject = this.season;
            ContentEditService.setContentSeasonBreadcrumbs();
        }
    };

    const getCriteria = (includeAll: boolean): JS5Object => {
        let criteria: JS5Object = {
            id: $routeParams.id,
            searchString: $location.search().searchText
        };

        if (includeAll) {
            criteria.fetchLimit = 0;
        } else {
            let offset = this.pagingOptions.pageSize * (this.pagingOptions.currentPage - 1);

            criteria.seasonId = $routeParams.seasonId || undefined;
            criteria.fetchLimit = this.pagingOptions.pageSize;
            criteria.fetchOffset = offset;
        }

        return criteria;
    };

    const getData = (includeAll: boolean = false): JS5Promise => {
        return CollaboratorsService.getContentActors(getCriteria(includeAll), handleGridDataQuery);
    };

    const saveCollaboratorRole = (actor: JS5Object): void => {
        CollaboratorsService.updateCollaboratorRole(actor);
    };

    const handleNewActorSelection = (newActors: JS5Object) => {
        this.loadingIndicator.show = true;
        if (this.isSeason && !_.isNil(this.season)) {
            // combine the added actors that the user selected with the existing series-level actors and create an
            // array of unique values based on id
            this.season.actors = _.unionWith(this.season.actors, newActors, (seasonActor: JS5Object, newActor: JS5Object) => {
                return _.isEqual(seasonActor.id, newActor.id)
            });
            // Save the entire season with the updated array of actors
            ContentEditService.saveSeason(this.season).then((savedSeason: JS5Object) => {
                // set the component's season to the saved season
                this.season = savedSeason;
                // refresh the data in the list
                getData(false);
            }).finally(() => {
                this.loadingIndicator.show = false;
            });

        } else {
            const actor: JS5Object = {
                type: 'Actor',
                collaborator: newActors[0],
                contentId: $routeParams.id
            };
            CollaboratorsService.newActor = actor;

            // if the collaborator is saved without a character, navigate to the actor edit view to assign a character
            if (_.isNil(_.get(actor, 'collaborator.character'))) {
                $location.url(`/meta/${contentPathParam}/edit/${$routeParams.id}/actor/edit/-1`);
            } else {
                this.loadingIndicator.show = false;
                // close the form and reload the grid
                getData(true);
            }
        }
    };

    const openAddCollaboratorModal = () => {
        CollaboratorsService.getContentActors(getCriteria(true), () => {
        }).then((collaborators: Array<JS5Object>) => {
            let availableCollaborators = collaborators;
            if (!this.isSeason) {
                availableCollaborators = undefined;
            }
            ModalService.showAddCollaboratorModal(
                availableCollaborators,
                'actors',
                handleNewActorSelection,
                undefined,
                !this.isSeason,
                !!this.isSeason,
                !!this.isSeries && !!this.isSeason,
                true
            );
        });
    };

    const openConfirmModal = (messageText: string, title: string, handler: JS5Function): void => {
        ModalService.showMessageModal({
            messageText: messageText,
            pullCancelLeft: true,
            title: title,
            windowClass: 'confirm-modal-sm',
            showOK: true
        }, handler);
    };

    const deleteCollaborators = (): void => {
        const selectedActorsToDelete = this.gridApi.selection.getSelectedRows();
        const message = 'Are you sure you want to delete Collaborator Role(s)? ';
        const title = 'Delete Collaborator Role(s)?';
        const resultHandler = (): void => {
            this.loadingIndicator.show = true;
            if (this.isSeason && !_.isNil(this.season)) {
                _.pullAllWith(this.season.actors, selectedActorsToDelete, (seasonActor: JS5Object, selectedActorToDelete: JS5Object) => {
                    return (_.isEqual(seasonActor.id, selectedActorToDelete.id));
                });
                // Save the entire season with the updated array of actors
                ContentEditService.saveSeason(this.season).then((savedSeason: JS5Object) => {
                    // set the component's season to the saved season
                    this.season = savedSeason;
                    // refresh the data in the list
                    getData(false);
                }).finally(() => {
                    this.loadingIndicator.show = false;
                })
            } else {
                CollaboratorsService.deleteCollaboratorRoles(selectedActorsToDelete).then(() => {
                    getData();
                    this.gridApi.selection.clearSelectedRows();
                }).finally(() => {
                    this.loadingIndicator.show = false;
                })
            }
        };

        openConfirmModal(message, title, resultHandler);
    };

    const onPaginationChanged = (): void => {
        getData();
    };

    const gridColumns = [
        {
            name: 'checkMarkRowHeaderCol',
            field: 'checkMark',
            width: '2%',
            enableColumnMenu: false,
            enableSorting: false,
            enableColumnResizing: false,
            cellTemplate: 'components/visual/grid/check-mark-template.html',
            headerCellTemplate: 'package/editor/components/view/collaborator/grid-checkbox-header-template.html'
        },
        {
            field: 'collaborator.displayName',
            width: '25%',
            displayName: 'Actor',
            sort: {
                direction: 'asc',
                priority: 0
            }
        },
        {
            field: 'collaborator.nickname',
            width: '25%',
            displayName: 'Nickname',
            enableColumnMenu: false
        },
        {
            field: 'character',
            width: '25%',
            displayName: 'Character',
            enableColumnMenu: false
        },
        {
            field: 'collaborator.iMDbNumber',
            enableColumnMenu: false,
            name: 'imdbColumn',
            displayName: 'IMDB#',
            width: '18%',
            enableSorting: false,
            enableColumnResizing: false,
            cellTemplate: 'components/visual/grid/hyperlink-template.html'
        },
        {
            field: 'disabled',
            enableColumnMenu: false,
            name: 'disabled',
            displayName: 'Disabled',
            width: '5%',
            enableSorting: false,
            enableColumnResizing: false,
            cellTemplate: `<div class="checkbox-grid-cell" ng-click="grid.appScope.$ctrl.checkMarkCellClicked(row, $event)">
                                <input ng-if="!grid.appScope.$ctrl.isSeason" type="checkbox" ng-checked="COL_FIELD"/>
                                <label ng-if="!grid.appScope.$ctrl.isSeason"></label>
                                <div ng-if="grid.appScope.$ctrl.isSeason">{{(!grid.appScope.$ctrl.isSeason) ? '' : (COL_FIELD === true) ? 'YES' : 'NO'}}</div>
                            </div>`
        }
    ];

    this.gridOptions = {
        rowSelection: true,
        enableRowHeaderSelection: false,
        enableSelectAll: true,
        enableColumnResizing: true,
        enablePaginationControls: false,
        enableSorting: true,
        enableColumnMenus: false,
        enableFullRowSelection: true,
        enableHorizontalScrollbar: uiGridConstants.ALWAYS,
        enableVerticalScrollbar: uiGridConstants.ALWAYS,
        rowHeight: 26,
        multiSelect: true,
        columnDefs: gridColumns,
        onRegisterApi: (gridApi: JS5Object) => {
            this.gridApi = gridApi;
        },
        rowTemplate: 'components/visual/grid/searchview-component-row-template.html',
        data: []
    };

    this.toolbarButtonDefs = [
        {
            label: 'New',
            enabled: () => true,
            onClick: openAddCollaboratorModal
        },
        {
            label: 'Delete',
            enabled: () => this.gridApi && this.gridApi.selection.getSelectedCount() > 0,
            onClick: deleteCollaborators
        }
    ];

    this.onRowDblClick = (row: JS5Object) => {
        if (!this.isSeason) {
            $location.url(`/meta/${contentPathParam}/edit/${$routeParams.id}/actor/edit/${row.entity.id}`);
        }
    };

    this.checkMarkCellClicked = (row: JS5Object) => {
        row.entity.disabled = !row.entity.disabled;
        saveCollaboratorRole(row.entity);
    };

    this.paginationPageSizeChanged = () => {
        this.pagingOptions.currentPage = 1;
        onPaginationChanged();
    };

    this.paginationPrevPageHandler = () => {
        --this.pagingOptions.currentPage;
        onPaginationChanged();
    };

    this.paginationNextPageHandler = () => {
        ++this.pagingOptions.currentPage;
        onPaginationChanged();
    };

    this.paginationDefs = {
        paginationPrevPageHandler: this.paginationPrevPageHandler,
        paginationNextPageHandler: this.paginationNextPageHandler,
        paginationPageSizeChanged: this.paginationPageSizeChanged,
        paginationJumpToPageHandler: onPaginationChanged,
        pagingOptions: PaginationService,
    };

    this.$onInit = (): void => {
        this.loadingIndicator = {
            backdrop: true,
            show: false,
            title: 'Working',
            message: 'Please wait...'
        };

        PaginationService.resetToDefaultValues();
        this.pagingOptions = PaginationService;
        NavbarUtilService.viewSearchFunction = getData;
        getData(false);
    };

    this.$onDestroy = (): void => {
        NavbarUtilService.viewSearchFunction = null;
    };
}

angular.module('prism-ui.components').component('contentCollaboratorGridView', {
    templateUrl: 'components/collaborators/content-collaborator-grid-view-template.html',
    bindings: {
        content: '<',
        season: '<'
    },
    controller: ContentCollaboratorGridViewController
});
